import { useState, useCallback, useRef } from 'react';
import { SUBMIT_CONTACT_MESSAGE, getContactMessageConfig } from '../graphql/mutations/contactMutations';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const CONTACT_FIELD_LIMITS = {
  name: 200,
  email: 254,
  phone: 50,
  message: 5000,
};
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Builds the input object for SubmitContactMessage from the contact form data.
 * Maps: name (required), email (required), phone (optional), message (required),
 * wantsStudioListing (toggle), consentGiven (required), source.
 */
export function buildSubmitContactMessageInput(formData) {
  const { name, email, phone, message, listStudio, consent } = formData;

  return {
    name: (name || '').trim(),
    email: (email || '').trim().toLowerCase(),
    phone: phone ? String(phone).trim() || null : null,
    message: (message || '').trim(),
    wantsStudioListing: listStudio === true,
    consentGiven: consent === true,
    source: 'website_contact',
  };
}

/**
 * Hook to submit the contact form to the public GraphQL API.
 * Uses fetch with x-api-key (no Cognito).
 *
 * @returns {{
 *   submitContactMessage: (formData: Object) => Promise<{ success: boolean, message?: string, contactID?: string }>,
 *   isSubmitting: boolean,
 *   error: string | null,
 *   clearError: () => void
 * }}
 */
export function useSubmitContactMessage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  const clearError = useCallback(() => setError(null), []);

  const submitContactMessage = useCallback(async (formData) => {
    if (submittingRef.current) {
      return { success: false, message: 'Die Nachricht wird bereits gesendet.' };
    }

    const { endpoint, apiKey } = getContactMessageConfig();

    if (!endpoint || !apiKey) {
      setError('API ist nicht konfiguriert. Bitte Endpoint und API-Key prüfen.');
      return { success: false, message: 'API nicht konfiguriert' };
    }

    const input = buildSubmitContactMessageInput(formData);

    if (!input.name) {
      setError('Bitte gib deinen Namen an.');
      return { success: false, message: 'Name ist ein Pflichtfeld.' };
    }
    if (input.name.length > CONTACT_FIELD_LIMITS.name) {
      setError(`Name darf maximal ${CONTACT_FIELD_LIMITS.name} Zeichen lang sein.`);
      return { success: false, message: 'Name zu lang.' };
    }
    if (!input.email || !EMAIL_REGEX.test(input.email)) {
      setError('Bitte eine gültige E-Mail-Adresse angeben.');
      return { success: false, message: 'Ungültige E-Mail.' };
    }
    if (input.email.length > CONTACT_FIELD_LIMITS.email) {
      setError('E-Mail-Adresse ist zu lang.');
      return { success: false, message: 'E-Mail zu lang.' };
    }
    if (input.phone && input.phone.length > CONTACT_FIELD_LIMITS.phone) {
      setError(`Telefonnummer darf maximal ${CONTACT_FIELD_LIMITS.phone} Zeichen lang sein.`);
      return { success: false, message: 'Telefonnummer zu lang.' };
    }
    if (!input.message) {
      setError('Bitte beschreibe kurz dein Anliegen.');
      return { success: false, message: 'Nachricht fehlt.' };
    }
    if (input.message.length > CONTACT_FIELD_LIMITS.message) {
      setError(`Nachricht darf maximal ${CONTACT_FIELD_LIMITS.message.toLocaleString('de-DE')} Zeichen lang sein.`);
      return { success: false, message: 'Nachricht zu lang.' };
    }
    if (!input.consentGiven) {
      setError('Bitte stimme der Verarbeitung deiner Daten zu.');
      return { success: false, message: 'Zustimmung fehlt.' };
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({
          query: SUBMIT_CONTACT_MESSAGE,
          variables: { input },
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        setError('Netzwerkfehler. Bitte später erneut versuchen.');
        return { success: false, message: 'Netzwerkfehler' };
      }

      const result = await response.json();
      const gqlErrors = Array.isArray(result.errors) ? result.errors : [];
      const payload = result.data?.submitContactMessage;

      // AppSync returns 200 + errors when e.g. response type fields are not authorized for API key.
      if (gqlErrors.length > 0 && !payload) {
        const message = 'Serverfehler. Bitte später erneut versuchen.';
        setError(message);
        return { success: false, message };
      }

      if (payload?.success) {
        setError(null);
        return {
          success: true,
          message: payload.message || 'Vielen Dank! Wir melden uns in Kürze.',
          contactID: payload.contactID,
        };
      }

      const message = payload?.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
      setError(message);
      return { success: false, message };
    } catch (err) {
      const message =
        err?.name === 'AbortError'
          ? 'Die Anfrage hat zu lange gedauert. Bitte versuche es erneut.'
          : 'Etwas ist schiefgelaufen. Bitte versuche es erneut.';
      setError(message);
      return { success: false, message };
    } finally {
      clearTimeout(timeoutId);
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, []);

  return {
    submitContactMessage,
    isSubmitting,
    error,
    clearError,
  };
}

export default useSubmitContactMessage;
